// src/encounter/services/use-medication-search.ts
// GET /api/Provider/GetMedication?ProviderId=&searchQuery= → ProviderMedication[] → DS
// MedicationResult[] for the A/P medication picker (PROD-1165). Provider catalog on the
// .NET monolith (dev-api, reachability-verified 401). ProviderId = the practitioner's
// user.id (same id the appointment self-filter uses). Min 2 chars to avoid empty-search
// spam; the consumer debounces input (appointments pattern). AbortSignal forwarded.
//
// Consumer wiring (feed AssessmentPlanView.medicationResults) lands with the encounter-shell
// A/P integration — gated on PROD-1144. Built ahead, like the rail's data hooks.
import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import { providerMedicationToOption, type MedicationOption } from '../lib/to-medication-result';
import type { GetMedicationResponse } from '@/types/provider-api';

const MIN_QUERY_LENGTH = 2;

// Returns MedicationOption[] (carries the coding `system` for CreateMedication, PROD-1257).
// MedicationOption is assignable to the DS MedicationResult, so this also serves the A/P picker.
export function useMedicationSearch(providerId: string | null | undefined, query: string) {
  const q = query.trim();
  return useQuery<MedicationOption[]>({
    queryKey: ['medication-search', providerId, q],
    enabled: !!providerId && q.length >= MIN_QUERY_LENGTH,
    queryFn: async ({ signal }) => {
      const res = await get<GetMedicationResponse>(
        `/api/Provider/GetMedication?ProviderId=${encodeURIComponent(providerId as string)}&searchQuery=${encodeURIComponent(q)}`,
        { signal },
      );
      return (res ?? []).filter((m) => !m.isDeleted).map(providerMedicationToOption);
    },
  });
}
